import { createBrowserRouter } from 'react-router'
import { RequireAuth, RedirectIfAuthenticated } from './auth/RequireAuth'
import { AppLayout } from './layouts/AppLayout'
import { AuthLayout } from './layouts/AuthLayout'
import { LoginScreen } from '@/features/auth/LoginScreen'
import { SignUpScreen } from '@/features/auth/SignUpScreen'
import { WaitlistScreen } from '@/features/auth/WaitlistScreen'
import { ForgotPasswordScreen } from '@/features/auth/ForgotPasswordScreen'
import { ResetPasswordScreen } from '@/features/auth/ResetPasswordScreen'
import { OnboardingAccounts } from '@/features/onboarding/OnboardingAccounts'
import { OnboardingCard } from '@/features/onboarding/OnboardingCard'
import { OnboardingReady } from '@/features/onboarding/OnboardingReady'
import { OpenAccountScreen } from '@/features/paywall/OpenAccountScreen'
import { OpenCardScreen } from '@/features/paywall/OpenCardScreen'
import { AddMoneyScreen } from '@/features/money/AddMoneyScreen'
import { ConvertScreen } from '@/features/money/ConvertScreen'
import { ReceiveScreen } from '@/features/money/ReceiveScreen'
import { TransactionsScreen } from '@/features/money/TransactionsScreen'
import { InsightsScreen } from '@/features/money/InsightsScreen'
import { PlansScreen } from '@/features/plans/PlansScreen'
import { CashbackScreen } from '@/features/plans/CashbackScreen'
import { ESimScreen } from '@/features/explore/ESimScreen'
import { TravelScreen } from '@/features/explore/TravelScreen'
import { WellnessScreen } from '@/features/explore/WellnessScreen'
import { CryptoWalletScreen } from '@/features/invest/CryptoWalletScreen'
import { InvestScreen } from '@/features/invest/InvestScreen'
import { VaultDetailScreen } from '@/features/invest/VaultDetailScreen'
import { OpenVaultScreen } from '@/features/invest/OpenVaultScreen'
import { CryptoBuyScreen } from '@/features/invest/CryptoBuyScreen'
import { CryptoReceiveScreen } from '@/features/invest/CryptoReceiveScreen'
import { CryptoConvertScreen } from '@/features/invest/CryptoConvertScreen'
import { CryptoAssetScreen } from '@/features/invest/CryptoAssetScreen'
import { EsimCheckoutScreen } from '@/features/explore/EsimCheckoutScreen'
import { EsimQrScreen } from '@/features/explore/EsimQrScreen'
import { HotelBookingScreen } from '@/features/explore/HotelBookingScreen'
import { FlightDetailScreen } from '@/features/explore/FlightDetailScreen'
import { WellnessBookingScreen } from '@/features/explore/WellnessBookingScreen'
import { GymScreen } from '@/features/explore/GymScreen'
import { CreditsScreen } from '@/features/system/CreditsScreen'
import { RealAssetsScreen } from '@/features/assets/RealAssetsScreen'
import { ReturnsCalculatorScreen } from '@/features/assets/ReturnsCalculatorScreen'
import { ProfileScreen } from '@/features/system/ProfileScreen'
import { TopUpScreen } from '@/features/money/TopUpScreen'
import { BuyUsdScreen } from '@/features/money/BuyUsdScreen'
import { HotelsScreen } from '@/features/explore/HotelsScreen'
import { FlightsScreen } from '@/features/explore/FlightsScreen'
import { PortfolioScreen } from '@/features/assets/PortfolioScreen'
import { SecondaryMarketScreen } from '@/features/assets/SecondaryMarketScreen'
import { HotelDetailScreen } from '@/features/explore/HotelDetailScreen'
import { FlightCheckoutScreen } from '@/features/explore/FlightCheckoutScreen'
import { WellnessVenueScreen } from '@/features/explore/WellnessVenueScreen'
import { AssetDetailScreen } from '@/features/assets/AssetDetailScreen'
import { InvestReviewScreen } from '@/features/assets/InvestReviewScreen'
import { DashboardScreen } from '@/features/dashboard/DashboardScreen'
import { AccountsScreen } from '@/features/accounts/AccountsScreen'
import { CardsScreen } from '@/features/cards/CardsScreen'
import { TransfersScreen } from '@/features/transfers/TransfersScreen'
import { SendAbroadScreen } from '@/features/transfers/SendAbroadScreen'
import { NotFoundScreen, PublicNotFoundScreen } from '@/features/system/NotFoundScreen'
import { SupportScreen } from '@/features/system/SupportScreen'
import { NotificationsScreen } from '@/features/notifications/NotificationsScreen'
import { SendReviewScreen } from '@/features/transfers/SendReviewScreen'
import { RecipientsScreen } from '@/features/transfers/RecipientsScreen'
import { ConvertReviewScreen } from '@/features/money/ConvertReviewScreen'
import { AccountDetailScreen } from '@/features/accounts/AccountDetailScreen'
import { CardDetailScreen } from '@/features/cards/CardDetailScreen'
import { TransactionDetailScreen } from '@/features/money/TransactionDetailScreen'
import { SettingsScreen } from '@/features/system/SettingsScreen'
import { LegalScreen } from '@/features/system/LegalScreen'
import {
  AccountOpenedScreen,
  AddMoneyDoneScreen,
  CardOpenedScreen,
  ConvertDoneScreen,
  SendDoneScreen,
  TopUpDoneScreen,
  VaultOpenedScreen,
  CryptoBoughtScreen,
  CryptoConvertedScreen,
  EsimBoughtScreen,
  HotelBookedScreen,
  FlightBookedScreen,
  WellnessBookedScreen,
  RealAssetInvestedScreen,
} from '@/features/flows/success-routes'

export const router = createBrowserRouter([
  {
    element: <RedirectIfAuthenticated />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: <LoginScreen /> },
          { path: '/signup', element: <SignUpScreen /> },
          { path: '/waitlist', element: <WaitlistScreen /> },
          { path: '/forgot-password', element: <ForgotPasswordScreen /> },
          { path: '/reset-password', element: <ResetPasswordScreen /> },
        ],
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      { path: '/onboarding/accounts', element: <OnboardingAccounts /> },
      { path: '/onboarding/card', element: <OnboardingCard /> },
      { path: '/onboarding/ready', element: <OnboardingReady /> },
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <DashboardScreen /> },
          { path: '/accounts', element: <AccountsScreen /> },
          { path: '/accounts/:id', element: <AccountDetailScreen /> },
          { path: '/cards', element: <CardsScreen /> },
          { path: '/cards/:id', element: <CardDetailScreen /> },
          { path: '/transfers', element: <TransfersScreen /> },
          { path: '/transfers/send', element: <SendAbroadScreen /> },
          { path: '/transfers/send/review', element: <SendReviewScreen /> },
          { path: '/transfers/recipients', element: <RecipientsScreen /> },
          { path: '/add-money', element: <AddMoneyScreen /> },
          { path: '/convert', element: <ConvertScreen /> },
          { path: '/convert/review', element: <ConvertReviewScreen /> },
          { path: '/top-up', element: <TopUpScreen /> },
          { path: '/buy-usd', element: <BuyUsdScreen /> },
          { path: '/receive', element: <ReceiveScreen /> },
          { path: '/transactions', element: <TransactionsScreen /> },
          { path: '/transactions/:id', element: <TransactionDetailScreen /> },
          { path: '/insights', element: <InsightsScreen /> },
          { path: '/open/account', element: <OpenAccountScreen /> },
          { path: '/open/card', element: <OpenCardScreen /> },
          { path: '/invest', element: <InvestScreen /> },
          { path: '/invest/done', element: <VaultOpenedScreen /> },
          { path: '/invest/:id/open', element: <OpenVaultScreen /> },
          { path: '/invest/:id', element: <VaultDetailScreen /> },
          { path: '/real-assets', element: <RealAssetsScreen /> },
          { path: '/real-assets/calculator', element: <ReturnsCalculatorScreen /> },
          { path: '/real-assets/portfolio', element: <PortfolioScreen /> },
          { path: '/real-assets/secondary', element: <SecondaryMarketScreen /> },
          { path: '/real-assets/review', element: <InvestReviewScreen /> },
          { path: '/real-assets/done', element: <RealAssetInvestedScreen /> },
          { path: '/real-assets/:id', element: <AssetDetailScreen /> },
          { path: '/crypto', element: <CryptoWalletScreen /> },
          { path: '/crypto/buy', element: <CryptoBuyScreen /> },
          { path: '/crypto/receive', element: <CryptoReceiveScreen /> },
          { path: '/crypto/convert', element: <CryptoConvertScreen /> },
          { path: '/crypto/done', element: <CryptoBoughtScreen /> },
          { path: '/crypto/convert/done', element: <CryptoConvertedScreen /> },
          { path: '/crypto/:symbol', element: <CryptoAssetScreen /> },
          { path: '/travel', element: <TravelScreen /> },
          { path: '/travel/hotels', element: <HotelsScreen /> },
          { path: '/travel/flights', element: <FlightsScreen /> },
          { path: '/travel/hotels/:id/book', element: <HotelBookingScreen /> },
          { path: '/travel/hotels/:id/done', element: <HotelBookedScreen /> },
          { path: '/travel/hotels/:id', element: <HotelDetailScreen /> },
          { path: '/travel/flights/done', element: <FlightBookedScreen /> },
          { path: '/travel/flights/:id', element: <FlightDetailScreen /> },
          { path: '/travel/flights/checkout', element: <FlightCheckoutScreen /> },
          { path: '/wellness/:id', element: <WellnessVenueScreen /> },
          { path: '/wellness', element: <WellnessScreen /> },
          { path: '/gym', element: <GymScreen /> },
          { path: '/wellness/:id/book', element: <WellnessBookingScreen /> },
          { path: '/wellness/:id/done', element: <WellnessBookedScreen /> },
          { path: '/esim', element: <ESimScreen /> },
          { path: '/esim/buy', element: <EsimCheckoutScreen /> },
          { path: '/esim/qr', element: <EsimQrScreen /> },
          { path: '/esim/done', element: <EsimBoughtScreen /> },
          { path: '/plans', element: <PlansScreen /> },
          { path: '/cashback', element: <CashbackScreen /> },
          { path: '/profile', element: <ProfileScreen /> },
          { path: '/support', element: <SupportScreen /> },
          { path: '/settings', element: <SettingsScreen /> },
          { path: '/legal/terms', element: <LegalScreen kind="terms" /> },
          { path: '/legal/privacy', element: <LegalScreen kind="privacy" /> },
          { path: '/legal/photography', element: <CreditsScreen /> },
          { path: '/notifications', element: <NotificationsScreen /> },
          { path: '/transfers/send/done', element: <SendDoneScreen /> },
          { path: '/convert/done', element: <ConvertDoneScreen /> },
          { path: '/add-money/done', element: <AddMoneyDoneScreen /> },
          { path: '/top-up/done', element: <TopUpDoneScreen /> },
          { path: '/open/account/done', element: <AccountOpenedScreen /> },
          { path: '/open/card/done', element: <CardOpenedScreen /> },
          { path: '*', element: <NotFoundScreen /> },
        ],
      },
    ],
  },
  { path: '*', element: <PublicNotFoundScreen /> },
])
